/**
 * Multi events menu: selectable list of events/custom events to go inside
 * mp-drop-menu
 */
import partition from 'lodash/partition';

import {pluralize} from 'common/util/string';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {useStore} from 'common/util/component-helpers';

import {EventsMenu} from '../events-menu';
import template from './index.jade';

export class MultiEventsMenu extends EventsMenu {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      inverted: `boolean`,
    };
  }

  get config() {
    return {
      ...super.config,
      template,

      defaultState: {
        ...super.config.defaultState,
        sectionItems: [],
        stagedSelected: new Set(),
      },

      helpers: {
        ...super.config.helpers,
        getCommitLabel: () => this.getCommitLabel(),
        handleItemClick: (ev) => this.handleItemClick(ev.detail.item),
        handleSelectAll: (ev) => this.handleSelectAll(ev),
        handleSelectAllMatching: (ev) => this.handleSelectAllMatching(ev),
        onCommitClick: () => this.onCommitClick(),
        canCommit: () => this.state.stagedSelected.size !== this.getEventItemsFromContext().length,
      },
    };
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);

    if (attr === `open`) {
      if (this.attr(`open`)) {
        this.updateEventItems();
      } else {
        this.resetStagedSelection();
        this.updateSections(this.getEventItemsFromContext(), true);
      }
    } else if (this.initialized && this.isConnected && (attr === `inverted` || attr === `selected`)) {
      this.handleSelectionChange();
    }
  }

  connectedCallback() {
    super.connectedCallback();

    this._onStoreCallback = () => this.handleSelectionChange();
    this.getContext(`eventsController`).providerStores.forEach((store) => useStore(this, store, this._onStoreCallback));
  }

  transformSelectedVal(val) {
    return new Set(val || []);
  }

  updateEventItems() {
    this.handleSelectionChange();
  }

  getEventItemsFromContext() {
    return this.getContext(`eventsController`).getSections({
      dataGroupId: this.attr(`data-group-id`),
      excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
      excludeCustomEvents: this.attr(`exclude-custom-events`),
      hideEditControls: true,
      includeTags: false,
    })[0].items;
  }

  get eventData() {
    return this.getEventItemsFromContext().map((sectionItem) => sectionItem.value);
  }

  handleSelectAll(ev) {
    const {selectionState} = ev.detail;
    const {sectionItems} = this.state;
    const selectedItems = selectionState === `all` ? [] : this.eventData.map((event) => event.value);

    this.update({stagedSelected: new Set(selectedItems)});
    this.updateSections(sectionItems);
  }

  handleSelectAllMatching(ev) {
    const {sectionItems, stagedSelected} = this.state;
    const matchingSections = ev.detail.matchingSections;

    const allMatchingEventsSelected = !matchingSections.some((section) =>
      section.items.some(({value: eventItem}) => !stagedSelected.has(eventItem.value)),
    );

    const updatedStagedSelected = new Set(stagedSelected);
    matchingSections.forEach((section) =>
      section.items.forEach(({value: eventItem}) =>
        allMatchingEventsSelected
          ? updatedStagedSelected.delete(eventItem.value)
          : updatedStagedSelected.add(eventItem.value),
      ),
    );

    this.update({stagedSelected: updatedStagedSelected});
    this.updateSections(sectionItems);
  }

  onCommitClick() {
    this.dispatchSelectedEvent();

    const selectionDetail = this.getSelectionDetail();
    this.trackSelection({
      eventName: `Hide events`,
      props: {
        action: selectionDetail.action,
        '# of hidden events': selectionDetail.selectedCount,
        '# of visible events': selectionDetail.unselectedCount,
      },
    });
  }

  handleItemClick(item) {
    const {sectionItems} = this.state;

    this.updateSelectedValues(item.value);
    this.updateSections(sectionItems);
  }

  handleSelectionChange() {
    this.resetStagedSelection();
    this.updateSections(this.getEventItemsFromContext(), true);
  }

  // when 'inverted' is set, the list takes
  // all events as being selected excluding the
  // ones not in `selected`
  resetStagedSelection() {
    let {selected} = this.state;

    if (this.attr(`inverted`)) {
      selected = this.eventData.filter((event) => !selected.has(event.value)).map((event) => event.value);
    }

    this.update({stagedSelected: new Set(selected)});
  }

  getSections() {
    if (this.getContext(`eventsController`).loading) {
      return [
        {
          isLoading: true,
          items: [],
        },
      ];
    }

    return [{items: this.state.sectionItems}];
  }

  // re-organizes current menu items by sorting
  // them by selection state
  updateSections(source, reorderItems = false) {
    const {stagedSelected} = this.state;

    let sectionItems = source.map((item) => ({
      ...item,
      isSelected: stagedSelected.has(item.value.value),
    }));

    if (reorderItems) {
      const [selected, unselected] = partition(sectionItems, (item) => item.isSelected);

      if (selected.length <= unselected.length) {
        sectionItems = selected.concat(unselected);
      } else {
        sectionItems = unselected.concat(selected);
      }
    }

    this.update({sectionItems});
  }

  // store selectedLabel in state as it must
  // be deferred to commited selection changes
  selectedLabel() {
    let suffix = ``;

    const selectedCount = this.attr(`selected`).length;

    if (this.attr(`inverted`)) {
      suffix = ` · all but ${selectedCount}`;
    } else if (selectedCount > 0) {
      suffix = ` · ${selectedCount}`;
    }

    return `Hide Events${suffix}`;
  }

  updateSelectedValues(eventItem) {
    const stagedSelected = new Set(this.state.stagedSelected);
    const value = eventItem.value;

    if (stagedSelected.has(value)) {
      stagedSelected.delete(value);
    } else {
      stagedSelected.add(value);
    }

    this.update({stagedSelected});
  }

  dispatchSelectedEvent() {
    const {stagedSelected} = this.state;
    const selectionDetail = this.getSelectionDetail();

    this.dispatchEvent(
      new CustomEvent(`select`, {
        detail: {
          ...selectionDetail,
          selected: Array.from(stagedSelected),
          unselected: this.eventData.filter((item) => !stagedSelected.has(item.value)).map((item) => item.value),
        },
      }),
    );
  }

  getCommitLabel() {
    const {selectedCount} = this.getSelectionDetail();

    if (selectedCount === 0) {
      return `Apply`;
    }

    return `Hide ${selectedCount} ${pluralize(`event`, selectedCount)}`;
  }

  getSelectionDetail(source) {
    const {stagedSelected} = this.state;
    const selectedCount = (source || stagedSelected).size;
    const unselectedCount = this.getEventItemsFromContext().length - selectedCount;
    let action = `hide`;

    // when selected items take most of the list
    // users are just showing a subset of events
    if (unselectedCount && unselectedCount < selectedCount) {
      action = `show`;
    }

    return {
      action,
      selectedCount,
      unselectedCount,
    };
  }
}

export const MultiEventsMenuEl = defineAndMakeJsxFactory(`mp-multi-events-menu`, MultiEventsMenu);
